"use client";

import { stepPosition } from "@/lib/learning-journey/step-mapping";

type MiniStepsProps = {
  readonly active: number;
  /**
   * The steps this phrase actually has, as absolute indices in ascending
   * order — `applicableStepIndices`. One segment is drawn per entry and the
   * counter reads within it, so a right-hand-only unit says « 2/3 » instead
   * of « 3/5 » with two slots that will never open. `active`, `reachable`
   * and `onJump` keep speaking absolute indices: they address states of the
   * FSM, not positions on a bar.
   */
  readonly steps?: ReadonlyArray<number>;
  readonly total?: number;
  readonly reachable?: ReadonlyArray<number>;
  readonly onJump?: (index: number) => void;
  /**
   * « Retour », one **sub-step** back. Absent when there is nothing behind
   * the current one, in which case the control is not drawn.
   *
   * It used to be computed from `reachable`, one *segment* back, and the two
   * are not the same distance: five segments fold nine states, so leaving the
   * two-hand practice landed on the right hand's grip and cost the player the
   * right hand's own run and the two hands together to get back. The FSM
   * walks the states; see `PhraseFSM.previousState`.
   */
  readonly onBack?: () => void;
  readonly label?: string;
  /**
   * Laid out in a row that already has a border and its own padding — the
   * band across the roll. Drops the rule above and the vertical breathing
   * that only make sense at the foot of a panel.
   */
  readonly inline?: boolean;
};

const DEFAULT_TOTAL = 7;

export function MiniSteps({
  active,
  steps,
  total = DEFAULT_TOTAL,
  reachable,
  onJump,
  onBack,
  label,
  inline = false,
}: MiniStepsProps) {
  const shown = steps ?? Array.from({ length: total }, (_, i) => i);
  const position = stepPosition(shown, active);
  const reachableSet = new Set(reachable ?? []);

  return (
    <div
      role="tablist"
      aria-label="Progression de la phrase"
      className={
        inline
          ? "flex items-center gap-3"
          : "flex items-center gap-3 px-4 py-3"
      }
      style={
        inline
          ? undefined
          : { borderTop: "1px solid var(--color-border-subtle)" }
      }
    >
      {onBack !== undefined && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Revenir à l'étape précédente"
          title="Étape précédente (←)"
          className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2"
          style={{
            color: "var(--color-text-secondary)",
            fontSize: 11,
            cursor: "pointer",
            border: "none",
            background: "transparent",
            ["--tw-ring-color" as string]: "var(--color-accent)",
          }}
        >
          <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
            ‹
          </span>
          Retour
        </button>
      )}

      <span
        className="font-mono text-[11px] tabular-nums shrink-0"
        style={{ color: "var(--color-text-secondary)" }}
      >
        {position}/{shown.length}
      </span>

      <div className="flex flex-1 items-center gap-1.5">
        {shown.map((idx, at) => {
          const isActive = idx === active;
          const isPast = idx < active;
          const isReachable = reachableSet.has(idx);
          const interactive =
            onJump !== undefined && isReachable && !isActive;

          return (
            <Segment
              key={idx}
              index={idx}
              position={at + 1}
              total={shown.length}
              isActive={isActive}
              isPast={isPast}
              interactive={interactive}
              onJump={onJump}
            />
          );
        })}
      </div>

      {label !== undefined && (
        <span
          className="font-mono text-[11px] uppercase shrink-0"
          style={{
            color: "var(--color-text-muted)",
            letterSpacing: "0.18em",
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

type SegmentProps = {
  /** Absolute step index: what `onJump` is given. */
  readonly index: number;
  /** Rank among the steps drawn: what the player is told. */
  readonly position: number;
  readonly total: number;
  readonly isActive: boolean;
  readonly isPast: boolean;
  readonly interactive: boolean;
  readonly onJump?: (index: number) => void;
};

function Segment({
  index,
  position,
  total,
  isActive,
  isPast,
  interactive,
  onJump,
}: SegmentProps) {
  const background = isActive
    ? "var(--color-accent)"
    : isPast
      ? "rgba(196, 181, 253, 0.45)"
      : "rgba(255, 255, 255, 0.08)";

  const boxShadow = isActive ? "0 0 6px var(--color-violet-glow)" : "none";

  const commonStyle: React.CSSProperties = {
    height: 3,
    borderRadius: 99,
    background,
    boxShadow,
    flex: isActive ? 2 : 1,
    transition: "flex 0.3s ease, background 0.2s ease",
  };

  if (interactive && onJump !== undefined) {
    return (
      <button
        type="button"
        role="tab"
        aria-label={`Aller à l'étape ${position} sur ${total}`}
        onClick={() => onJump(index)}
        className="cursor-pointer rounded-full focus:outline-none focus-visible:ring-2"
        style={{
          ...commonStyle,
          border: "none",
          padding: 0,
          ["--tw-ring-color" as string]: "var(--color-accent)",
        }}
      />
    );
  }

  return (
    <div
      role="tab"
      aria-current={isActive ? "step" : undefined}
      aria-label={`Étape ${position} sur ${total}`}
      style={commonStyle}
    />
  );
}
